<div class="fullcontainer pr-3">
  <div class='sheet'>
    <div class='titleContainer'>
      <h1 class='header'>Report - Movement History</h1>
      <div>
        <button class='btn btn-secondary' routerLink='/warehousing/reports'>All Reports</button>
      </div>
    </div>

    <p class='text-muted'>
      The audit trail. The ledger is append-only, so nothing here can be edited or removed —
      a mistake is corrected by posting a further movement, and both rows stay visible.
    </p>

    <div class='row'>
      <div class='col-md-4'>
        <label>Start Date</label>
        <input type='date' class='form-control'
          [class.is-invalid]='this.datesInvalid'
          [(ngModel)]='this.searchFilters.startDate' />
      </div>
      <div class='col-md-4'>
        <label>End Date</label>
        <input type='date' class='form-control'
          [class.is-invalid]='this.datesInvalid'
          [(ngModel)]='this.searchFilters.endDate' />
        @if (this.datesInvalid) {
          <div class='invalid-feedback d-block'>The end date is before the start date.</div>
        }
      </div>
      <div class='col-md-4'>
        <label>Movement Type</label>
        <select class='form-control' [(ngModel)]='this.searchFilters.movementType'>
          <option value=''>All types</option>
          @for (item of this.movementTypes; track item.value) {
            <option [value]='item.value'>{{ item.label }}</option>
          }
        </select>
      </div>
      <div class='col-md-4'>
        <label>Reference</label>
        <select class='form-control' [(ngModel)]='this.searchFilters.referenceId'>
          <option [value]='-1'>All references</option>
          @for (item of this.references; track item.id) {
            <option [value]='item.id'>{{ item.name }} ({{ item.code }})</option>
          }
        </select>
      </div>
      <div class='col-md-4'>
        <label>Warehouse</label>
        <select class='form-control' (change)='this.onWarehouseChanged($event.target.value)'>
          <option value='-1'>All warehouses</option>
          @for (item of this.warehouses; track item.id) {
            <option [value]='item.id'>{{ item.name }}</option>
          }
        </select>
      </div>
      <div class='col-md-4'>
        <label>Location</label>
        <!-- Deliberately disabled until a warehouse is chosen: locations are scoped by
             warehouseId and the same aisle/shelf can exist at two sites. -->
        <select class='form-control'
          [disabled]='this.searchFilters.warehouseId === -1'
          [(ngModel)]='this.searchFilters.warehouseLocationId'>
          <option [value]='-1'>All locations</option>
          @for (item of this.locations; track item.id) {
            <option [value]='item.id'>{{ item.aisle }} / {{ item.shelf }}</option>
          }
        </select>
      </div>
      <div class='col-md-4'>
        <label>Product</label>
        <select class='form-control' [(ngModel)]='this.searchFilters.productId'>
          <option [value]='-1'>All products</option>
          @for (item of this.products; track item.id) {
            <option [value]='item.id'>{{ item.name }}</option>
          }
        </select>
      </div>
    </div>
    <br />
    <br />
    <button class='btn btn-primary' (click)='this.loadReport()'
      [disabled]='this.running || this.datesInvalid'>Run Report</button>
    <hr />
    <button class='btn btn-secondary' (click)='exportToCSV()' [disabled]='this.movements.length === 0'>Export to CSV</button>
  </div>

  <div class='sheet'>
    @if (this.running) {
      <p class='m-0'><i class="fas fa-spinner fa-spin"></i> Running&hellip;</p>
    }

    @if (this.hasRun && !this.running && this.movements.length === 0) {
      <p class='m-0 text-muted'>No movements matched those filters.</p>
    }

    @if (this.movements.length > 0) {
      <div class='titleContainer'>
        <h1 class='header'>Results</h1>
      </div>

      <!-- Signed, so this is the net effect on stock rather than the volume moved. A
           transfer contributes a matched pair that cancels out, which is correct. -->
      <h3>NET QTY MOVED: {{ this.getNetQty() }}</h3>
      <p class='text-muted'>
        {{ this.movements.length }} movement(s). Quantities are signed, so a transfer nets
        to zero across its two rows.
      </p>

      <!-- The net weight and how much of the report it covers, always together. Most movements are
           never weighed, so the total alone is a real number that describes an unknown fraction of
           what is on screen — the plausible figure with no coverage beside it is the mistake this
           module has already had to write up. The unweighed count is drawn even when it is zero,
           because "the total covers everything" is a statement worth making and a figure that only
           appears when something is wrong cannot be told from one that was left out. -->
      <h3>NET WEIGHT MOVED: {{ this.getNetWeight() }}</h3>
      <p class='text-muted'>
        @if (this.getWeighedMovements() > 0) {
          Weighed at both ends: {{ this.getWeighedMovements() }} movement(s).
        } @else {
          No movement in this report was weighed at both ends, so there is no net weight to show.
        }
        Not weighed, or weighed once: {{ this.getUnweighedMovements() }} movement(s) — these are not
        in the total. A transfer is one weighing recorded on two rows, so only its outward row counts
        towards the figure. Weights are recorded for reporting and are checked against nothing: an end
        weight below the start is the expected reading on stock going out.
      </p>

      <table class='table'>
        <thead>
          <th>Id</th>
          <th>Date</th>
          <th>Product</th>
          <th>SKU</th>
          <th>Warehouse</th>
          <th>Aisle</th>
          <th>Shelf</th>
          <th>Type</th>
          <th class='text-right'>Qty</th>
          <th class='text-right'>Start Weight</th>
          <th class='text-right'>End Weight</th>
          <th class='text-right'>Net Weight</th>
          <th>Reference</th>
          <th>Reference Code</th>
          <th>Created By</th>
        </thead>
        <tbody>
          @for (movement of this.movements; track movement.id) {
            <tr>
              <td>{{ movement.id }}</td>
              <td>{{ this.formatDateTime(movement.createdAt) }}</td>
              <td>{{ movement.product ? movement.product.name : '' }}</td>
              <td>{{ movement.product ? movement.product.sku : '' }}</td>
              <td>{{ this.warehouseNameOf(movement) }}</td>
              <td>{{ movement.warehouseLocation ? movement.warehouseLocation.aisle : '' }}</td>
              <td>{{ movement.warehouseLocation ? movement.warehouseLocation.shelf : '' }}</td>
              <td>{{ this.getMovementTypeLabel(movement.movementType) }}</td>
              <td class='text-right' [class.text-danger]='this.isNegative(movement.qty)'>
                {{ this.formatQty(movement.qty) }}
              </td>
              <!-- Blank on a movement that was not weighed, and blank on the net where only one
                   reading was taken. Not marked in red when the net is negative, unlike the qty
                   beside it: a falling weight is what a despatch looks like, so colouring it would
                   report the ordinary case as a problem. -->
              <td class='text-right'>{{ this.formatWeight(movement.startWeight) }}</td>
              <td class='text-right'>{{ this.formatWeight(movement.endWeight) }}</td>
              <td class='text-right'>{{ this.formatNetWeight(movement) }}</td>
              <td>{{ movement.reference ? movement.reference.name : '' }}</td>
              <td>{{ movement.reference ? movement.reference.code : '' }}</td>
              <td>{{ movement.createdBy }}</td>
            </tr>
          }
        </tbody>
      </table>
    }
  </div>
</div>
